import { Component, ElementRef, AfterViewChecked, ViewChild, OnInit, Input, OnChanges, SimpleChanges, ChangeDetectorRef, ViewEncapsulation, HostListener } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChatService } from '../chat.service';
import {Message, SwirlService} from '../swirl.service';
import { HttpErrorResponse } from '@angular/common/http';
import {AuthService} from "../../auth/auth.service";
import {ConfigService} from "../config.service";

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ChatComponent implements OnInit, OnChanges, AfterViewChecked {
  @ViewChild('dummy') private dummy!: ElementRef; // Reference to the dummy div
  @ViewChild('feedbackOptions') feedbackOptions!: ElementRef<HTMLElement>;
  @ViewChild('thanksBanner') thanksBanner!: ElementRef<HTMLElement>;
  @Input() messages: any[] = [];
  @Input() ragRatingEnabled: boolean = false;
  parsedMessages: any[] = [];
  suggestions = [
    'I don\'t like the style',
    'It\'s incorrect',
    'It didn\'t answer my question',
    'It\'s too long',
    'It\'s too biased',
    'Contains unsafe information',
    'Took too long to answer'
  ];
  thumbsUpIcon          = 'assets/images/icon_thumbs_up.png';
  thumbsUpActiveIcon    = 'assets/images/icon_thumbs_up_active.png';
  thumbsDownIcon        = 'assets/images/icon_thumbs_down.png';
  thumbsDownActiveIcon  = 'assets/images/icon_thumbs_down_active.png';

  private shouldScroll: boolean = false;
  private feedbackJustOpened = false;
  private thanksJustOpened = false;
  private followUpsEnabled: boolean = true;

  constructor(
    private sanitizer: DomSanitizer,
    private chatService: ChatService,
    private swirlService: SwirlService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private configService: ConfigService
  ) { }

  ngOnInit(): void {
    this.parseMessages();
    if (this.authService.getRagRatingStatus()) {
      this.ragRatingEnabled = true;
    }
    this.configService.getConfig('default').subscribe(configResponse => {
      if (configResponse) {
          if (configResponse.followUpsEnabled !== undefined) {
              this.followUpsEnabled = configResponse.followUpsEnabled;
          }
      }
    });
  }

  ngAfterViewChecked() {
    if (this.shouldScroll) {
      requestAnimationFrame(() => {
        this.scrollToBottom();
        this.shouldScroll = false;
      });
    }

    // Feedback‐box scroll:
    if (this.feedbackJustOpened && this.feedbackOptions) {
      requestAnimationFrame(() => {
        this.feedbackOptions.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        this.feedbackJustOpened = false;
      });
    }

    // Thanks‐banner scroll:
    if (this.thanksJustOpened && this.thanksBanner) {
      requestAnimationFrame(() => {
        this.thanksBanner.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        this.thanksJustOpened = false;
      });
    }
  }

  private scrollToBottom(): void {
    try {
      this.dummy.nativeElement.scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      console.error('Scroll to bottom error:', err);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.messages) {
      const prev: Message[] = changes.messages.previousValue || [];
      const curr: Message[] = changes.messages.currentValue || [];

      // always re-parse
      this.parseMessages();
      this.cdr.detectChanges(); // Ensure change detection runs

      // only scroll if we actually got a new message
      this.shouldScroll = curr.length > prev.length; // Flag to trigger scrolling

    }
  }

  parseMessages(): void {
    if (this.messages) {
      this.parsedMessages = this.messages.map((message) => {
        // Step 1: Main message formatting
        const converted = this.convertCustomMarkupToHtml(message.content);
        const sanitizedContent = this.formatContentExceptPre(converted);
        const cleanedContent = this.cleanUnwantedHtml(sanitizedContent);
        const fixedContent = this.sanitizeSourceLinks(cleanedContent);

        let augmentedHtml = fixedContent;

        // Step 2: Inject followUps block (if available)
        const rawFollowUps = message.additional_content?.followUps;

        // Make sure it's an array, then flatten it by 1 (or Infinity if you want to
        // handle any number of nesting levels).
        const followUps = Array.isArray(rawFollowUps)
          ? rawFollowUps.flat(Infinity)
          : [];

        if (followUps.length > 0 && this.followUpsEnabled) {
          let followUpsHtml = `<div class="followups-list">
                                <p class="followups-header">Follow-up Questions:</p>
                                <ul class="followups-list-ul">`;
          followUps.forEach((item: any) => {
            const question = item?.question || '';
            followUpsHtml += `<li class="followups-list-li">
                                <a class="question-link">${question}</a>
                              </li>`;
          });
          followUpsHtml += `</ul></div>`;
          augmentedHtml += followUpsHtml;
        }

        // Step 3: Clean sources (remove <em> tags)
        if (
            message.additional_content &&
            Array.isArray(message.additional_content.sources)
        ) {
          message.additional_content.sources = message.additional_content.sources.map((source: any) => ({
            ...source,
            url: (source.url || '').replace(/<em>/gi, '').replace(/<\/em>/gi, ''),
            title: (source.title || '').replace(/<em>/gi, '').replace(/<\/em>/gi, '')
          }));
        }

        // Step 4: Return the parsed message
        return {
          ...message,
          content: this.sanitizer.bypassSecurityTrustHtml(augmentedHtml) as SafeHtml,
        };
      });
    }
  }

  private cleanUnwantedHtml(html: string): string {
    // Create a DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find and remove <p><br><b></b></p> elements
    doc.querySelectorAll('p').forEach((p) => {
      if (p.innerHTML.trim() === '<br><b></b>') {
        p.remove();
      }
    });

    return doc.body.innerHTML;
  }

  // Sanitize the source links by encoding the href attribute
  private sanitizeSourceLinks(html: string): string {
    return html.replace(/(<a\b(?=[^>]*\bsources-list-link\b)[^>]*\bhref=")([^"]+)(")/gi, 
      (match, p1, p2, p3) => {
        // Remove any <em> tags from the captured URL
        let cleanUrl = p2.replace(/<em>/gi, '').replace(/<\/em>/gi, '');
        try {
          new URL(cleanUrl);
          cleanUrl = encodeURI(cleanUrl);
        } catch (e) {
          cleanUrl = cleanUrl.replace(/ /g, '%20');
        }
        return p1 + cleanUrl + p3;
      }
    );
  }

  private convertCustomMarkupToHtml(input: string): string {
    // Normalize newlines
    input = input.replace(/\r\n/g, '\n');

    const lines = input.split('\n');
    const out: string[] = [];

    // indicates whether we're in a code block and helps to collect lines of code
    let inCode = false;
    // collects lines of code between code fences
    let codeBuf: string[] = [];
    // LLM usually outputs code with a single backtick or triple backticks followed by language name
    let codeFenceType: 'single' | 'triple' | null = null;
    // default language
    let codeLang: string = 'plaintext';
    // collects lines of normal text (non-code blocks)
    let paraBuf: string[] = [];

    // Fences
    const TRIPLE_OPEN_RE  = /^\s*(?:```|''')\s*([a-z0-9+#.\-]+)?\s*$/i;
    const TRIPLE_CLOSE_RE = /^\s*(?:```|''')\s*$/;
    const SINGLE_OPEN_RE  = /^\s*`\s*([a-z0-9+#.\-]+)\s*$/i;  // one backtick + language on its own line
    const SINGLE_CLOSE_RE = /^\s*`\s*$/;

    // Simple passthrough to preserve existing HTML <pre>…</pre> blocks
    let inHtmlPre = false;
    let htmlPreBuf: string[] = [];
    const PRE_OPEN_RE  = /<\s*pre\b[^>]*>/i;
    const PRE_CLOSE_RE = /<\/\s*pre\s*>/i;

    // HTML escape
    const esc = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Normalize language to a safe/highlighter-friendly class name
    const normalizeLang = (raw?: string): string => {
      if (!raw) return 'plaintext';
      let l = raw.toLowerCase();

      // quick synonym map
      const map: Record<string, string> = {
        js: 'javascript', node: 'javascript',
        ts: 'typescript',
        py: 'python',
        rb: 'ruby',
        sh: 'bash', zsh: 'bash', shell: 'bash',
        yml: 'yaml',
        'c++': 'cpp', cpp: 'cpp',
        'c#': 'csharp', cs: 'csharp',
        'objective-c': 'objectivec', objc: 'objectivec', 'objectivec': 'objectivec',
        ps1: 'powershell', pwsh: 'powershell',
        txt: 'plaintext', text: 'plaintext',
        htm: 'html'
      };
      l = map[l] || l;

      // keep only safe chars for class names
      l = l.replace(/[^a-z0-9+\-]/g, '');
      return l || 'plaintext';
    };

    // Link helpers
    const cleanHref = (raw: string): string | null => {
      let href = raw.replace(/<\/?em>/gi, '').replace(/&amp;/g, '&').trim();
      if (!/^https?:\/\//i.test(href)) return null;
      try { href = encodeURI(decodeURI(href)); } catch { href = href.replace(/ /g, '%20'); }
      return href;
    };
    const anchorHtml = (url: string, label: string) =>
        `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`;

    // Render a normal paragraph with inline formatting + links
    const renderParagraph = (text: string): string => {
      // 1) Tokenize links BEFORE escaping
      const aTokens: string[] = [];
      const token = (i: number) => `__A_TOKEN_${i}__`;

      // Markdown links: [label](url) → <a href="...">label</a>
      text = text.replace(
          /\[([^\]]+)\]\(\s*(https?:\/\/[^\s)]+)\s*\)/gi,
          (_m, label, url) => {
            const href = cleanHref(url);
            const safeLabel = esc(label); // escape label to avoid injection
            const html = href ? anchorHtml(href, safeLabel) : safeLabel;
            aTokens.push(html);
            return token(aTokens.length - 1);
          }
      );

      // Existing anchor tags <a>…</a>: preserve inner text as label
      text = text.replace(
          /<a\b([^>]*)href="([^"]+)"([^>]*)>([\s\S]*?)<\/a>/gi,
          (_m, _a1, url, _a2, inner) => {
            const href = cleanHref(url);
            // strip any tags inside and escape
            const label = inner ? esc(inner.replace(/<[^>]*>/g, '')) : 'source';
            const finalLabel = label.trim() ? label : 'source';
            const html = href ? anchorHtml(href, finalLabel) : finalLabel;
            aTokens.push(html);
            return token(aTokens.length - 1);
          }
      );

      // 2) Escape & apply inline markup
      let html = esc(text);
      //  **bold** followed by one or more newlines → <strong>bold</strong><br> (repeat per newline)
      html = html.replace(/\*\*(.+?)\*\*([ \t]*\n+)/g, (_m, bold, nl) => {
        const breaks = (nl.match(/\n/g) || []).length;
        return `<strong>${bold}</strong>` + '<br>'.repeat(breaks);
      });
      // Remaining **bold** (no newline immediately after) → <strong>bold</strong>
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/`(.+?)`/g, '<em>$1</em>');               // `italic`
      html = html.replace(/^---\s*$/gm, '<hr>');                    // horizontal rule

      // 3) Restore link tokens
      html = html.replace(/__A_TOKEN_(\d+)__/g, (_m, i) => aTokens[Number(i)]);

      // Preserve any remaining newlines in the paragraph
      html = html.replace(/\n/g, '<br>');

      return `<p>${html}</p>`;
    };

    const cleanHeadingText = (s: string): string => {
      // Remove bold markers (**…**) and inline-code backticks while preserving content
      // e.g., "**Hello** `world`" -> "Hello world"
      return s
          .replace(/\*\*(.*?)\*\*/g, '$1') // strip bold wrappers
          .replace(/`+/g, '')              // remove any backticks
          .trim();
    };

    // Headings: support #..###### (first line only)
    const tryRenderHeading = (block: string): string | null => {
      const trimmed = block.trim();
      const m = trimmed.match(/^#{1,6}\s+/);
      if (!m) return null;

      const level = Math.min(Math.max(m[0].trim().length, 1), 6);
      // remove leading #'s and one space
      let after = trimmed.replace(/^#{1,6}\s+/, '');
      const nlIdx = after.indexOf('\n');

      let headingText: string;
      let remainder = '';

      if (nlIdx >= 0) {
        headingText = after.slice(0, nlIdx).trim();
        remainder   = after.slice(nlIdx + 1);
      } else {
        // Handle "Heading: 1. List ..." on the same line
        const split = after.match(/^(.*?:)\s*(\d+\.\s[\s\S]*)$/);
        if (split) { headingText = split[1].trim(); remainder = split[2]; }
        else        headingText = after.trim();
      }

      // Strip **…** and `..` in heading text
      headingText = cleanHeadingText(headingText);

      const h = `<h${level}>${esc(headingText)}</h${level}>`;
      if (remainder.trim()) {
        return h + '\n' + renderParagraph(remainder);
      }
      return h;
    };

    const flushParagraph = () => {
      if (!paraBuf.length) return;
      const block = paraBuf.join('\n');

      // Try heading 1–6
      const headingSplit = block.split(/(?=^#{1,6}\s+)/m);

      for (const chunk of headingSplit) {
        const trimmed = chunk.trim();
        if (!trimmed) continue;

        const headingHtml = tryRenderHeading(trimmed);
        if (headingHtml) {
          out.push(headingHtml);
        } else {
          out.push(renderParagraph(trimmed));
        }
      }

      paraBuf = [];
    };

    const flushCode = () => {
      const lang = normalizeLang(codeLang);
      out.push(`<pre><code class="language-${lang}">${esc(codeBuf.join('\n'))}</code></pre>`);
      inCode = false;
      codeBuf = [];
      codeFenceType = null;
      codeLang = 'plaintext';
    };

    // HANDLE <pre>…</pre> blocks
    for (const line of lines) {
      // Pass through existing HTML <pre>…</pre> blocks verbatim
      if (!inCode) {
        if (inHtmlPre) {
          htmlPreBuf.push(line);
          if (PRE_CLOSE_RE.test(line)) {
            out.push(htmlPreBuf.join('\n'));
            inHtmlPre = false;
            htmlPreBuf = [];
          }
          continue;
        } else if (PRE_OPEN_RE.test(line)) {
          flushParagraph();
          inHtmlPre = true;
          htmlPreBuf = [line];
          if (PRE_CLOSE_RE.test(line)) { // handle single-line <pre>…</pre>
            out.push(htmlPreBuf.join('\n'));
            inHtmlPre = false;
            htmlPreBuf = [];
          }
          continue;
        }
      }

      // OPEN fences
      if (!inCode) {
        let m: RegExpExecArray | null;
        // triple fence with optional language
        if ((m = TRIPLE_OPEN_RE.exec(line))) {
          flushParagraph();
          inCode = true;
          codeFenceType = 'triple';
          codeBuf = [];
          codeLang = m[1] ? m[1] : 'plaintext';
          continue;
        }
        // single fence with required language
        if ((m = SINGLE_OPEN_RE.exec(line))) {
          flushParagraph();
          inCode = true;
          codeFenceType = 'single';
          codeBuf = [];
          codeLang = m[1];
          continue;
        }
      }

      // CLOSE fences
      if (inCode) {
        if (
            (codeFenceType === 'triple' && TRIPLE_CLOSE_RE.test(line)) ||
            (codeFenceType === 'single' && SINGLE_CLOSE_RE.test(line))
        ) {
          flushCode();
          continue;
        }
        // otherwise collect code
        codeBuf.push(line);
        continue;
      }

      // Not in code: paragraph handling
      if (line.trim() === '') {
        flushParagraph();
      } else {
        paraBuf.push(line);
      }
    }

    // Flush leftovers
    if (inCode) flushCode();
    else if (inHtmlPre) out.push(htmlPreBuf.join('\n')); // unclosed <pre>…: still pass through
    else flushParagraph();

    return out.join('\n');
  }

  getFaviconUrl(url: string): string {
    const cleanUrl = url.replace(/<em>/gi, '').replace(/<\/em>/gi, '');
    try {
      const domain = new URL(cleanUrl).origin;
      return `${domain}/favicon.ico`;
    } catch {
      return '';
    }
  }

  handleFaviconError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none'; // Hide the image if it fails to load
  }

  formatContent(content: string): string {
    return content
      .replace(/>\n+/g, '>')   // Remove newlines after opening tags
      .replace(/\n/g, '<br>')  // Replace remaining newlines
      .replace(/^(<br\s*\/?>)+/, '');
  }

  private formatContentExceptPre(html: string): string {
    // 1) Temporarily replace <pre>...</pre> with tokens so we don't touch code blocks
    const preBlocks: string[] = [];
    const token = (i: number) => `__PRE_BLOCK_${i}__`;

    const preserved = html.replace(/<pre[\s\S]*?<\/pre>/gi, (m) => {
      preBlocks.push(m);
      return token(preBlocks.length - 1);
    });

    // 2) Apply formatting to NON-pre content
    let formatted = preserved
        .replace(/>\n+/g, '>')
        .replace(/\n/g, '<br>')
        .replace(/^(<br\s*\/?>)+/, '');

    // 3) Unescape only ALLOWED inline tags outside <pre>
    const allowed = ['em'];
    const tagRe = new RegExp(`&lt;(/?)(${allowed.join('|')})&gt;`, 'gi');
    formatted = formatted.replace(tagRe, (_m, slash, tag) => `<${slash}${tag}>`);

    // 4) Restore <pre> blocks
    return formatted.replace(/__PRE_BLOCK_(\d+)__/g, (_, i) => preBlocks[Number(i)]);
  }



  @HostListener('click', ['$event'])
  handleLinkClick(event: Event) {
    const target = event.target as HTMLElement;
    const questionLink = target.closest('.question-link') as HTMLElement;

    // We only submit a question if not already loading
    this.chatService.isLoading$.subscribe(isLoading => {
      if (questionLink && !isLoading) {
        event.preventDefault();
        const question = questionLink.textContent?.trim();
        if (question) {
          this.sendFollowUpQuestion(question);
        }
      }
    }).unsubscribe();
  }

  sendFollowUpQuestion(question: string) {
    const chatId = this.chatService.getChatId();
    if (chatId === undefined) {
      console.error('No active chat session found.');
      return;
    }

    const userMessage = { role: 'user', content: question };
    this.chatService.addMessage(chatId, userMessage);

    // Force reorder so it jumps to top
    const now = new Date().toISOString();
    this.chatService.notifyChatUpdated(chatId, now);

    // Re-emit the same chatId to cause ChatHistory to highlight & scroll
    this.chatService.setChatId(chatId);

    // Send to server
    this.chatService.setLoading(true);
    this.sendMessage(question, chatId);
  }

  sendMessage(query: string, chatId: number): void {
    this.swirlService.sendMessage(query, chatId).subscribe({
      next: (response) => {
        // Add the assistant message
        this.chatService.addMessage(chatId, {
          id: response.id,
          role: 'assistant',
          time: response.time,
          content: response.content,
          additional_content: response.additional_content,
          rating: null as 0|1|null,
          rated: false,
          show_feedback_box: false,
          feedback_text: '',
          show_thanks_message: false,
          show_other_modal: false,
        });
        // Re-emit chatId so ChatHistory updates
        this.chatService.setChatId(chatId);

        // Reorder chat
        const updatedAt = new Date().toISOString();
        this.chatService.notifyChatUpdated(chatId, updatedAt);

        // Done loading
        this.chatService.setLoading(false);
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error sending follow-up question:', error);
        const errorMessage = {
          role: 'system',
          content: 'Sorry, there was an error processing your request. Please try again later.',
        };
        this.chatService.addMessage(chatId, errorMessage);
        this.chatService.setLoading(false);
      },
    });
  }

  trackByMessageId(index: number, message: any): number {
    return message.id;
  }

  onThumbsUp(message: any): void {
    if (message.rated) { return; }
    const chatId = this.chatService.getChatId();
    if (chatId === undefined) {
      console.error('No active chat session found.');
      return;
    }
    this.swirlService.rateResult(message.id, 1).subscribe({
      next: () => {
        this.chatService.patchMessage(chatId, message.id, {
          rated: true,
          rating: 1,
          show_thanks_message: true
        });
        this.thanksJustOpened = true;
        setTimeout(() => {
          this.chatService.patchMessage(chatId, message.id, {
            show_thanks_message: false
          });
        }, 3000);
      },
      error: err => console.error('API Validation Errors:', err.error)
    });
  }

  onThumbsDown(message: any): void {
    if (message.rated) { return; }
    const chatId = this.chatService.getChatId();
    if (chatId === undefined) {
      console.error('No active chat session found.');
      return;
    }
    this.chatService.patchMessage(chatId, message.id, {
      rating: 0,
      show_feedback_box: true
    });
    this.feedbackJustOpened = true;
  }

  openOtherModal(message: any): void {
    if (message.rated) { return; }
    const chatId = this.chatService.getChatId();
    if (chatId === undefined) {
      console.error('No active chat session found.');
      return;
    }
    this.chatService.patchMessage(chatId, message.id, {
      show_other_modal: true
    });
  }

  closeOtherModal(message: any): void {
    const chatId = this.chatService.getChatId();
    if (chatId === undefined) {
      console.error('No active chat session found.');
      return;
    }
    this.chatService.patchMessage(chatId, message.id, {
      show_other_modal: false,
      feed_back_text: ''
    });
  }

  submitFeedback(message: any, feedback: string): void {
    if (message.rated || !feedback.trim()) { return; }
    const chatId = this.chatService.getChatId();
    if (chatId === undefined) {
      console.error('No active chat session found.');
      return;
    }
    this.swirlService.rateResult(message.id, 0, feedback.trim()).subscribe({
      next: () => {
        this.chatService.patchMessage(chatId, message.id, {
          rated: true,
          show_feedback_box: false,
          show_thanks_message: true,
          feedback_text: feedback.trim(),
        });
        this.thanksJustOpened = true;
        setTimeout(() => {
          this.chatService.patchMessage(chatId, message.id, {
            show_thanks_message: false
          });
        }, 3000);
      },
      error: err => console.error('API Validation Errors:', err.error)
    });
  }
}
